import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AccessModule,
  AccessOverviewResponse,
  DepartmentAccess,
  Employee,
  Permission,
  assignRoleToEmployee,
  createAccessDepartment,
  createAccessModule,
  createAccessPermission,
  createAccessRole,
  createAccessUser,
  deleteAccessDepartment,
  deleteAccessModule,
  deleteAccessPermission,
  deleteAccessRole,
  deleteAccessUser,
  deleteRoleDepartmentAccess,
  getAccessOverview,
  getAccessUsers,
  grantPermissionToRole,
  revokePermissionFromRole,
  revokeRoleFromEmployee,
  updateAccessDepartment,
  updateAccessModule,
  updateAccessPermission,
  updateAccessRole,
  updateAccessUser,
  upsertRoleDepartmentAccess,
} from '../services/api';

type AccessEntity = 'user' | 'role' | 'module' | 'permission' | 'department' | 'employeeRole' | 'rolePermission';
type ModalMode = 'create' | 'edit' | 'delete';

type AccessModalState = {
  entity: AccessEntity;
  mode: ModalMode;
  id?: number;
} | null;

type LoadOverviewOptions = {
  /** Обновить данные без полноэкранной загрузки (не сбрасывает прокрутку страницы). */
  silent?: boolean;
};

type UserSortKey = 'username' | 'full_name' | 'email' | 'roles' | 'expenses_approver' | 'is_active';
type UserSortDirection = 'asc' | 'desc';
type ReferenceSortDirection = 'asc' | 'desc';
type RoleSortKey = 'id' | 'name' | 'description' | 'is_head';
type ModuleSortKey = 'id' | 'name' | 'code' | 'description';
type DepartmentSortKey = 'id' | 'name' | 'code' | 'is_executor';

/** Сравнение module_id из API и значения фильтра (число/строка из JSON или select). */
function sameNumericId(a: unknown, b: unknown): boolean {
  if (Number.isNaN(Number(a)) || Number.isNaN(Number(b))) {
    return false;
  }
  return Number(a) === Number(b);
}

/** Код разрешения для колонок матрицы VIEW/CREATE/… (пробелы, регистр из справочника). */
function normalizeMatrixPermissionCode(raw: string | undefined | null): string {
  return String(raw ?? '').trim().toUpperCase();
}

function isDepartmentAccessActive(entry: DepartmentAccess | undefined): boolean {
  return Boolean(entry);
}

const AccessControlPage: React.FC = () => {
  const [overview, setOverview] = useState<AccessOverviewResponse | null>(null);
  const [users, setUsers] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [roleForm, setRoleForm] = useState({ name: '', description: '', is_head: false });
  const [moduleForm, setModuleForm] = useState({ name: '', code: '', description: '' });
  const [permissionForm, setPermissionForm] = useState({ module_id: 0, code: 'VIEW', description: '' });
  const [departmentForm, setDepartmentForm] = useState({ name: '', code: '', is_executor: false });
  const [modalState, setModalState] = useState<AccessModalState>(null);
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    full_name: '',
    role: 'user',
    is_active: true,
    password: '',
    expenses_approver_id: null as number | null,
  });

  const [employeeRoleForm, setEmployeeRoleForm] = useState({ employeeId: 0, roleId: 0 });
  const [rolePermissionForm, setRolePermissionForm] = useState({ roleId: 0, permissionId: 0 });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(0);
  const [employeeRoleSearch, setEmployeeRoleSearch] = useState('');
  const [selectedModuleFilter, setSelectedModuleFilter] = useState(0);
  const [rolePermissionDraft, setRolePermissionDraft] = useState<Record<string, boolean>>({});
  const [isSavingRolePermissions, setIsSavingRolePermissions] = useState(false);
  const [isEnsuringMatrixPermission, setIsEnsuringMatrixPermission] = useState(false);
  /** Не сбрасывать черновик матрицы при следующем обновлении overview (локально добавили permission). */
  const skipDraftResetRef = useRef(false);
  const [departmentAccessDraft, setDepartmentAccessDraft] = useState<Record<string, boolean>>({});
  const [isSavingDepartmentAccess, setIsSavingDepartmentAccess] = useState(false);
  const [userSort, setUserSort] = useState<{ key: UserSortKey; direction: UserSortDirection }>({
    key: 'full_name',
    direction: 'asc',
  });
  const [roleSort, setRoleSort] = useState<{ key: RoleSortKey; direction: ReferenceSortDirection }>({
    key: 'id',
    direction: 'asc',
  });
  const [moduleSort, setModuleSort] = useState<{ key: ModuleSortKey; direction: ReferenceSortDirection }>({
    key: 'id',
    direction: 'asc',
  });
  const [departmentSort, setDepartmentSort] = useState<{ key: DepartmentSortKey; direction: ReferenceSortDirection }>({
    key: 'name',
    direction: 'asc',
  });

  const loadOverview = async (options?: LoadOverviewOptions): Promise<void> => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      const [data, usersData] = await Promise.all([getAccessOverview(), getAccessUsers()]);
      const normalized: AccessOverviewResponse = {
        ...data,
        roles: data.roles.map((r) => ({
          ...r,
          is_head: r.is_head === true,
        })),
        departments: data.departments.map((d) => ({
          ...d,
          is_executor: d.is_executor === true,
        })),
      };
      setOverview(normalized);
      setUsers(usersData);

      if (data.modules.length > 0) {
        setPermissionForm((prev) => ({ ...prev, module_id: prev.module_id || data.modules[0].id }));
      }
      if (data.employees.length > 0 && data.roles.length > 0) {
        setEmployeeRoleForm((prev) => ({
          employeeId: prev.employeeId || data.employees[0].id,
          roleId: prev.roleId || data.roles[0].id,
        }));
        setSelectedEmployeeId((prev) => prev || data.employees[0].id);
      }
      if (data.roles.length > 0 && data.permissions.length > 0) {
        setRolePermissionForm((prev) => ({
          roleId: prev.roleId || data.roles[0].id,
          permissionId: prev.permissionId || data.permissions[0].id,
        }));
      }
      if (data.roles.length > 0 && data.departments.length > 0) {
        setSelectedModuleFilter((prev) => prev || 0);
      }
    } catch (e) {
      setError('Не удалось загрузить данные по доступам. Убедитесь, что вы вошли как администратор.');
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const employeeRoleMap = useMemo(() => {
    const map = new Map<number, number[]>();
    overview?.employee_roles.forEach((entry) => {
      const current = map.get(entry.employee_id) || [];
      current.push(entry.role_id);
      map.set(entry.employee_id, current);
    });
    return map;
  }, [overview]);

  const rolePermissionMap = useMemo(() => {
    const map = new Map<number, number[]>();
    overview?.role_permissions.forEach((entry) => {
      const current = map.get(entry.role_id) || [];
      current.push(entry.permission_id);
      map.set(entry.role_id, current);
    });
    return map;
  }, [overview]);

  /** Серверные назначения + несохранённые правки матрицы — для сводки по модулям. */
  const effectiveRolePermissionMap = useMemo(() => {
    if (!overview) {
      return new Map<number, number[]>();
    }
    const result = new Map<number, number[]>();
    for (const role of overview.roles) {
      const serverIds = new Set(rolePermissionMap.get(role.id) || []);
      const ids: number[] = [];
      for (const permission of overview.permissions) {
        const key = `${role.id}:${permission.id}`;
        const inDraft = Object.prototype.hasOwnProperty.call(rolePermissionDraft, key);
        const granted = inDraft ? Boolean(rolePermissionDraft[key]) : serverIds.has(permission.id);
        if (granted) {
          ids.push(permission.id);
        }
      }
      result.set(role.id, ids);
    }
    return result;
  }, [overview, rolePermissionMap, rolePermissionDraft]);

  const permissionById = useMemo(() => {
    const map = new Map<number, Permission>();
    overview?.permissions.forEach((permission) => map.set(permission.id, permission));
    return map;
  }, [overview]);

  const moduleById = useMemo(() => {
    const map = new Map<number, AccessModule>();
    overview?.modules.forEach((module) => map.set(module.id, module));
    return map;
  }, [overview]);

  const selectedEmployee = useMemo(
    () => overview?.employees.find((employee) => employee.id === selectedEmployeeId) ?? null,
    [overview, selectedEmployeeId],
  );

  const selectedEmployeeRoleIds = useMemo(
    () => (selectedEmployee ? employeeRoleMap.get(selectedEmployee.id) || [] : []),
    [employeeRoleMap, selectedEmployee],
  );

  const selectedEmployeePermissionGroups = useMemo(() => {
    const grouped = new Map<string, string[]>();
    selectedEmployeeRoleIds.forEach((roleId) => {
      (rolePermissionMap.get(roleId) || []).forEach((permissionId) => {
        const permission = permissionById.get(permissionId);
        if (!permission) {
          return;
        }
        const moduleCode = moduleById.get(permission.module_id)?.code || 'UNKNOWN_MODULE';
        const currentCodes = grouped.get(moduleCode) || [];
        if (!currentCodes.includes(permission.code)) {
          currentCodes.push(permission.code);
        }
        grouped.set(moduleCode, currentCodes);
      });
    });
    return Array.from(grouped.entries()).map(([moduleCode, permissions]) => ({
      moduleCode,
      permissions: permissions.sort(),
    }));
  }, [moduleById, permissionById, rolePermissionMap, selectedEmployeeRoleIds]);

  const selectedEmployeeDepartmentAccess = useMemo(() => {
    const roleIdSet = new Set(selectedEmployeeRoleIds);
    return (
      overview?.department_access
        .filter((item) => roleIdSet.has(item.role_id))
        .map((item) => ({
          ...item,
          roleName: overview.roles.find((role) => role.id === item.role_id)?.name || `#${item.role_id}`,
          departmentName:
            overview.departments.find((department) => department.id === item.department_id)?.name ||
            `#${item.department_id}`,
        })) || []
    );
  }, [overview, selectedEmployeeRoleIds]);

  /** Одна строка на роль: отделы через запятую. */
  const selectedEmployeeDepartmentAccessGrouped = useMemo(() => {
    const collator = new Intl.Collator('ru', { sensitivity: 'base' });
    const byRole = new Map<number, { roleName: string; departments: string[] }>();
    selectedEmployeeDepartmentAccess.forEach((item) => {
      const existing = byRole.get(item.role_id);
      if (!existing) {
        byRole.set(item.role_id, { roleName: item.roleName, departments: [item.departmentName] });
      } else if (!existing.departments.includes(item.departmentName)) {
        existing.departments.push(item.departmentName);
      }
    });
    return Array.from(byRole.entries()).map(([roleId, { roleName, departments }]) => ({
      roleId,
      roleName,
      departmentsText: departments.slice().sort((a, b) => collator.compare(a, b)).join(', '),
    }));
  }, [selectedEmployeeDepartmentAccess]);

  const filteredEmployeeRoleRows = useMemo(() => {
    const query = employeeRoleSearch.trim().toLowerCase();
    if (!query || !overview) {
      return overview?.employee_roles || [];
    }

    return overview.employee_roles.filter((item) => {
      const employeeName = overview.employees.find((employee) => employee.id === item.employee_id)?.full_name || '';
      const roleName = overview.roles.find((role) => role.id === item.role_id)?.name || '';
      return `${employeeName} ${roleName}`.toLowerCase().includes(query);
    });
  }, [employeeRoleSearch, overview]);

  const sortedRoles = useMemo(() => {
    if (!overview) return [];
    const collator = new Intl.Collator('ru', { sensitivity: 'base' });
    const rows = [...overview.roles];
    rows.sort((a, b) => {
      let result = 0;
      if (roleSort.key === 'id') {
        result = a.id - b.id;
      } else if (roleSort.key === 'name') {
        result = collator.compare(a.name || '', b.name || '');
      } else if (roleSort.key === 'is_head') {
        result = Number(Boolean(a.is_head)) - Number(Boolean(b.is_head));
      } else {
        result = collator.compare(a.description || '', b.description || '');
      }
      return roleSort.direction === 'asc' ? result : -result;
    });
    return rows;
  }, [overview, roleSort]);

  const sortedModules = useMemo(() => {
    if (!overview) return [];
    const collator = new Intl.Collator('ru', { sensitivity: 'base' });
    const rows = [...overview.modules];
    rows.sort((a, b) => {
      let result = 0;
      if (moduleSort.key === 'id') {
        result = a.id - b.id;
      } else if (moduleSort.key === 'name') {
        result = collator.compare(a.name || '', b.name || '');
      } else if (moduleSort.key === 'code') {
        result = collator.compare(a.code || '', b.code || '');
      } else {
        result = collator.compare(a.description || '', b.description || '');
      }
      return moduleSort.direction === 'asc' ? result : -result;
    });
    return rows;
  }, [overview, moduleSort]);

  const sortedDepartments = useMemo(() => {
    if (!overview) return [];
    const collator = new Intl.Collator('ru', { sensitivity: 'base' });
    const rows = [...overview.departments];
    rows.sort((a, b) => {
      let result = 0;
      if (departmentSort.key === 'id') {
        result = a.id - b.id;
      } else if (departmentSort.key === 'name') {
        result = collator.compare(a.name || '', b.name || '');
      } else if (departmentSort.key === 'is_executor') {
        result = Number(Boolean(a.is_executor)) - Number(Boolean(b.is_executor));
      } else {
        result = collator.compare(a.code || '', b.code || '');
      }
      return departmentSort.direction === 'asc' ? result : -result;
    });
    return rows;
  }, [overview, departmentSort]);

  const employeeNameById = useMemo(() => {
    const map = new Map<number, string>();
    users.forEach((user) => map.set(user.id, user.full_name));
    overview?.employees.forEach((employee) => {
      if (!map.has(employee.id)) {
        map.set(employee.id, employee.full_name);
      }
    });
    return map;
  }, [users, overview]);

  const sortedUsers = useMemo(() => {
    const collator = new Intl.Collator('ru', { sensitivity: 'base' });
    const nextUsers = [...users];
    nextUsers.sort((a, b) => {
      const rolesA = (employeeRoleMap.get(a.id) || [])
        .map((roleId) => overview?.roles.find((r) => r.id === roleId)?.name || '')
        .filter(Boolean)
        .sort((x, y) => collator.compare(x, y))
        .join(', ');
      const rolesB = (employeeRoleMap.get(b.id) || [])
        .map((roleId) => overview?.roles.find((r) => r.id === roleId)?.name || '')
        .filter(Boolean)
        .sort((x, y) => collator.compare(x, y))
        .join(', ');

      const approverA = a.expenses_approver_id
        ? employeeNameById.get(a.expenses_approver_id) || ''
        : '';
      const approverB = b.expenses_approver_id
        ? employeeNameById.get(b.expenses_approver_id) || ''
        : '';

      let result = 0;
      if (userSort.key === 'is_active') {
        result = Number(Boolean(a.is_active)) - Number(Boolean(b.is_active));
      } else if (userSort.key === 'roles') {
        result = collator.compare(rolesA, rolesB);
      } else if (userSort.key === 'expenses_approver') {
        result = collator.compare(approverA, approverB);
      } else if (userSort.key === 'email') {
        result = collator.compare(a.email || '', b.email || '');
      } else if (userSort.key === 'username') {
        result = collator.compare(a.username || '', b.username || '');
      } else {
        result = collator.compare(a.full_name || '', b.full_name || '');
      }

      if (result === 0) {
        result = collator.compare(a.full_name || '', b.full_name || '');
      }
      return userSort.direction === 'asc' ? result : -result;
    });
    return nextUsers;
  }, [users, overview, employeeRoleMap, employeeNameById, userSort]);

  const toggleUserSort = (key: UserSortKey): void => {
    setUserSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const toggleRoleSort = (key: RoleSortKey): void => {
    setRoleSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleModuleSort = (key: ModuleSortKey): void => {
    setModuleSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleDepartmentSort = (key: DepartmentSortKey): void => {
    setDepartmentSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  useEffect(() => {
    if (!overview) return;
    if (skipDraftResetRef.current) {
      skipDraftResetRef.current = false;
      return;
    }
    const nextDraft: Record<string, boolean> = {};
    overview.role_permissions.forEach((item) => {
      nextDraft[`${item.role_id}:${item.permission_id}`] = true;
    });
    setRolePermissionDraft(nextDraft);
  }, [overview]);

  useEffect(() => {
    if (!overview) return;
    const next: Record<string, boolean> = {};
    overview.department_access.forEach((item) => {
      if (isDepartmentAccessActive(item)) {
        next[`${item.role_id}:${item.department_id}`] = true;
      }
    });
    setDepartmentAccessDraft(next);
  }, [overview]);

  const actionCodes: Array<'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'EXPORT'> = [
    'VIEW',
    'CREATE',
    'EDIT',
    'DELETE',
    'EXPORT',
  ];

  const permissionByCodeForSelectedModule = useMemo(() => {
    const map = new Map<string, number[]>();
    if (!overview || selectedModuleFilter === 0) {
      return map;
    }
    const sourcePermissions = overview.permissions.filter((permission) =>
      sameNumericId(permission.module_id, selectedModuleFilter),
    );

    sourcePermissions.forEach((permission) => {
      const normCode = normalizeMatrixPermissionCode(permission.code);
      if (!normCode) {
        return;
      }
      const current = map.get(normCode) || [];
      current.push(permission.id);
      map.set(normCode, current);
    });
    return map;
  }, [overview, selectedModuleFilter]);

  /** Создаёт недостающие строки разрешений в справочнике для колонки матрицы (пустой модуль → можно ставить галочку). */
  const ensurePermissionIdsForMatrixColumn = async (code: string): Promise<number[]> => {
    if (!overview || selectedModuleFilter === 0) {
      return [];
    }
    const norm = normalizeMatrixPermissionCode(code);
    if (!norm) {
      return [];
    }
    const moduleIds = [Number(selectedModuleFilter)];

    let snapshot = [...overview.permissions];
    const collectedIds: number[] = [];

    for (const mid of moduleIds) {
      if (Number.isNaN(mid)) {
        continue;
      }
      const existing = snapshot.find(
        (p) => sameNumericId(p.module_id, mid) && normalizeMatrixPermissionCode(p.code) === norm,
      );
      if (existing) {
        collectedIds.push(existing.id);
        continue;
      }
      try {
        setError(null);
        const created = await createAccessPermission({
          module_id: mid,
          code: norm,
          description: '',
        });
        snapshot = [...snapshot, created];
        skipDraftResetRef.current = true;
        setOverview((prev) =>
          prev ? { ...prev, permissions: [...prev.permissions, created] } : prev,
        );
        collectedIds.push(created.id);
      } catch {
        try {
          skipDraftResetRef.current = true;
          const [data, usersData] = await Promise.all([getAccessOverview(), getAccessUsers()]);
          setOverview(data);
          setUsers(usersData);
          snapshot = [...data.permissions];
          const refreshed = snapshot.find(
            (p) => sameNumericId(p.module_id, mid) && normalizeMatrixPermissionCode(p.code) === norm,
          );
          if (refreshed) {
            collectedIds.push(refreshed.id);
          }
        } catch {
          setError('Не удалось создать или загрузить разрешение для матрицы.');
        }
      }
    }

    return collectedIds;
  };

  const handleRolePermissionMatrixToggle = async (
    roleId: number,
    code: 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'EXPORT',
    nextChecked: boolean,
  ): Promise<void> => {
    let permissionIds = permissionByCodeForSelectedModule.get(code) || [];

    if (permissionIds.length === 0 && nextChecked) {
      setIsEnsuringMatrixPermission(true);
      try {
        permissionIds = await ensurePermissionIdsForMatrixColumn(code);
      } finally {
        setIsEnsuringMatrixPermission(false);
      }
      if (permissionIds.length === 0) {
        return;
      }
    }

    if (permissionIds.length === 0) {
      return;
    }

    setRolePermissionDraft((prev) => {
      const next = { ...prev };
      permissionIds.forEach((permissionId) => {
        next[`${roleId}:${permissionId}`] = nextChecked;
      });
      return next;
    });
  };

  const roleModuleSummaryRows = useMemo(() => {
    if (!overview) return [];
    return overview.roles.map((role) => {
      const permissionIds = effectiveRolePermissionMap.get(role.id) || [];
      const codesByModule = new Map<number, string[]>();
      permissionIds.forEach((permissionId) => {
        const permission = permissionById.get(permissionId);
        if (!permission) return;
        const list = codesByModule.get(permission.module_id) || [];
        if (!list.includes(permission.code)) {
          list.push(permission.code);
        }
        codesByModule.set(permission.module_id, list);
      });
      return { role, codesByModule };
    });
  }, [overview, permissionById, effectiveRolePermissionMap]);

  const onSaveRolePermissionMatrix = async (): Promise<void> => {
    if (!overview || selectedModuleFilter === 0) return;
    try {
      setError(null);
      setIsSavingRolePermissions(true);
      for (const role of overview.roles) {
        for (const code of actionCodes) {
          const permissionIds = permissionByCodeForSelectedModule.get(code) || [];
          for (const permissionId of permissionIds) {
            const key = `${role.id}:${permissionId}`;
            const currentValue = Boolean(rolePermissionMap.get(role.id)?.includes(permissionId));
            const nextValue = Boolean(rolePermissionDraft[key]);
            if (currentValue === nextValue) continue;
            if (nextValue) {
              await grantPermissionToRole(role.id, permissionId);
            } else {
              await revokePermissionFromRole(role.id, permissionId);
            }
          }
        }
      }
      await loadOverview({ silent: true });
    } catch (err) {
      setError('Не удалось сохранить изменения назначений разрешений.');
    } finally {
      setIsSavingRolePermissions(false);
    }
  };

  const onSaveDepartmentAccessMatrix = async (): Promise<void> => {
    if (!overview) return;
    try {
      setError(null);
      setIsSavingDepartmentAccess(true);
      for (const role of overview.roles) {
        for (const dept of overview.departments) {
          const key = `${role.id}:${dept.id}`;
          const entry = overview.department_access.find(
            (item) => item.role_id === role.id && item.department_id === dept.id,
          );
          const serverActive = isDepartmentAccessActive(entry);
          const desired = Object.prototype.hasOwnProperty.call(departmentAccessDraft, key)
            ? Boolean(departmentAccessDraft[key])
            : serverActive;
          if (desired === serverActive) continue;

          if (desired) {
            await upsertRoleDepartmentAccess(role.id, dept.id);
          } else if (entry) {
            await deleteRoleDepartmentAccess(role.id, dept.id);
          }
        }
      }
      await loadOverview({ silent: true });
    } catch {
      setError('Не удалось сохранить правила доступа к отделам.');
    } finally {
      setIsSavingDepartmentAccess(false);
    }
  };

  const onCreateUser = async (): Promise<void> => {
    await createAccessUser(userForm);
  };

  const onDeleteRole = async (roleId: number): Promise<void> => {
    try {
      setError(null);
      await deleteAccessRole(roleId);
      setModalState(null);
      await loadOverview({ silent: true });
    } catch (err) {
      setError('Не удалось удалить роль.');
    }
  };

  const onDeleteModule = async (moduleId: number): Promise<void> => {
    try {
      setError(null);
      await deleteAccessModule(moduleId);
      setModalState(null);
      await loadOverview({ silent: true });
    } catch (err) {
      setError('Не удалось удалить модуль.');
    }
  };

  const onDeletePermission = async (permissionId: number): Promise<void> => {
    try {
      setError(null);
      await deleteAccessPermission(permissionId);
      setModalState(null);
      await loadOverview({ silent: true });
    } catch (err) {
      setError('Не удалось удалить разрешение.');
    }
  };

  const onDeleteDepartment = async (departmentId: number): Promise<void> => {
    try {
      setError(null);
      await deleteAccessDepartment(departmentId);
      setModalState(null);
      await loadOverview({ silent: true });
    } catch (err) {
      setError('Не удалось удалить отдел.');
    }
  };

  const openRoleEditModal = (roleId: number): void => {
    const role = overview?.roles.find((item) => item.id === roleId);
    if (!role) {
      return;
    }
    setRoleForm({
      name: role.name,
      description: role.description ?? '',
      is_head: role.is_head === true,
    });
    setModalState({ entity: 'role', mode: 'edit', id: roleId });
  };

  const openModuleEditModal = (moduleId: number): void => {
    const module = overview?.modules.find((item) => item.id === moduleId);
    if (!module) {
      return;
    }
    setModuleForm({
      name: module.name,
      code: module.code,
      description: module.description || '',
    });
    setModalState({ entity: 'module', mode: 'edit', id: moduleId });
  };

  const openDepartmentEditModal = (departmentId: number): void => {
    const department = overview?.departments.find((item) => item.id === departmentId);
    if (!department) {
      return;
    }
    setDepartmentForm({
      name: department.name,
      code: department.code,
      is_executor: department.is_executor === true,
    });
    setModalState({ entity: 'department', mode: 'edit', id: departmentId });
  };

  const onSubmitEditModal = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!modalState || modalState.mode !== 'edit') {
      return;
    }

    const editId = modalState.id;
    if (editId === undefined) {
      setError('Не указан идентификатор записи.');
      return;
    }

    try {
      setError(null);
      if (modalState.entity === 'user') {
        await updateAccessUser(editId, {
          ...userForm,
          password: userForm.password || undefined,
        });
      } else if (modalState.entity === 'role') {
        await updateAccessRole(editId, {
          name: roleForm.name.trim(),
          description: roleForm.description.trim() ? roleForm.description.trim() : undefined,
          is_head: Boolean(roleForm.is_head),
        });
      } else if (modalState.entity === 'module') {
        await updateAccessModule(editId, moduleForm);
      } else if (modalState.entity === 'permission') {
        await updateAccessPermission(editId, permissionForm);
      } else {
        await updateAccessDepartment(editId, {
          name: departmentForm.name.trim(),
          code: departmentForm.code.trim(),
          is_executor: Boolean(departmentForm.is_executor),
        });
      }
      setModalState(null);
      await loadOverview({ silent: true });
    } catch (err) {
      setError('Не удалось сохранить изменения.');
    }
  };

  const onConfirmDelete = async (): Promise<void> => {
    if (!modalState || modalState.mode !== 'delete') {
      return;
    }
    try {
      setError(null);
      if (!modalState.id) {
        return;
      }
      if (modalState.entity === 'user') {
        await deleteAccessUser(modalState.id);
      } else if (modalState.entity === 'role') {
        await onDeleteRole(modalState.id);
      } else if (modalState.entity === 'module') {
        await onDeleteModule(modalState.id);
      } else if (modalState.entity === 'permission') {
        await onDeletePermission(modalState.id);
      } else if (modalState.entity === 'department') {
        await onDeleteDepartment(modalState.id);
      } else if (modalState.entity === 'employeeRole') {
        const entry = overview?.employee_roles.find((item) => item.id === modalState.id);
        if (entry) {
          await revokeRoleFromEmployee(entry.employee_id, entry.role_id);
        }
      } else if (modalState.entity === 'rolePermission') {
        const entry = overview?.role_permissions.find((item) => item.id === modalState.id);
        if (entry) {
          await revokePermissionFromRole(entry.role_id, entry.permission_id);
        }
      }

      // Единое поведение: после успешного удаления закрываем модалку для всех таблиц.
      setModalState(null);
      await loadOverview({ silent: true });
    } catch (err) {
      setError('Не удалось удалить запись.');
    }
  };

  const onSubmitCreateModal = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!modalState || modalState.mode !== 'create') return;
    try {
      if (modalState.entity === 'user') await onCreateUser();
      if (modalState.entity === 'employeeRole') await assignRoleToEmployee(employeeRoleForm.employeeId, employeeRoleForm.roleId);
      if (modalState.entity === 'rolePermission') await grantPermissionToRole(rolePermissionForm.roleId, rolePermissionForm.permissionId);
      if (modalState.entity === 'role') {
        await createAccessRole(roleForm);
        setRoleForm({ name: '', description: '', is_head: false });
      }
      if (modalState.entity === 'module') await createAccessModule(moduleForm);
      if (modalState.entity === 'permission') await createAccessPermission(permissionForm);
      if (modalState.entity === 'department') {
        await createAccessDepartment(departmentForm);
        setDepartmentForm({ name: '', code: '', is_executor: false });
      }
      setModalState(null);
      await loadOverview({ silent: true });
    } catch {
      setError('Не удалось создать запись.');
    }
  };

  if (isLoading) {
    return <div className="loading">⏳ Загрузка модели доступов...</div>;
  }

  if (error) {
    return (
      <div className="access-page">
        <h1>Управление доступами</h1>
        <div className="error-banner">{error}</div>
        <button type="button" className="primary-button" onClick={() => void loadOverview()}>
          Повторить
        </button>
      </div>
    );
  }

  if (!overview) {
    return null;
  }

  return (
    <div className="access-page">
      <div className="access-page__header">
        <h1>Управление доступами</h1>
        <div />
      </div>

      <section className="access-section"><h2>1. Пользователи</h2></section>
      <section className="access-grid access-grid--wide">
        <article className="access-card">
          <div className="card-header-row"><h2>Пользователи</h2><button type="button" className="secondary-button" onClick={() => setModalState({ entity: 'user', mode: 'create' })}>Добавить</button></div>
          <div className="matrix-wrap">
            <table className="matrix-table matrix-table--users">
              <thead>
                <tr>
                  <th className="col-left sortable" onClick={() => toggleUserSort('username')}>Логин {userSort.key === 'username' ? (userSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-left sortable" onClick={() => toggleUserSort('full_name')}>ФИО {userSort.key === 'full_name' ? (userSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-left sortable" onClick={() => toggleUserSort('email')}>Email {userSort.key === 'email' ? (userSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-left sortable" onClick={() => toggleUserSort('roles')}>Роли {userSort.key === 'roles' ? (userSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-left sortable" onClick={() => toggleUserSort('expenses_approver')}>Согласующий расходы {userSort.key === 'expenses_approver' ? (userSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleUserSort('is_active')}>Активен {userSort.key === 'is_active' ? (userSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-left">Действия</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => (
                  <tr key={u.id}>
                    <td className="col-left">{u.username}</td>
                    <td className="col-left">{u.full_name}</td>
                    <td className="col-left">{u.email}</td>
                    <td className="col-left">
                      {(employeeRoleMap.get(u.id) || [])
                        .map((roleId) => overview.roles.find((r) => r.id === roleId)?.name || '')
                        .filter(Boolean)
                        .sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' }))
                        .join(', ') || '-'}
                    </td>
                    <td className="col-left">
                      {u.expenses_approver_id
                        ? employeeNameById.get(u.expenses_approver_id) || '—'
                        : '—'}
                    </td>
                    <td>{u.is_active ? 'Да' : 'Нет'}</td>
                    <td className="col-left">
                      <button type="button" className="action-letter-btn action-letter-btn--edit" title="Редактировать" aria-label="Редактировать" onClick={() => { setUserForm({ username: u.username, email: u.email || '', full_name: u.full_name, role: u.role || 'user', is_active: Boolean(u.is_active), password: '', expenses_approver_id: u.expenses_approver_id ?? null }); setModalState({ entity: 'user', mode: 'edit', id: u.id }); }}>E</button>
                      <button type="button" className="action-letter-btn action-letter-btn--delete" title="Удалить" aria-label="Удалить" onClick={() => setModalState({ entity: 'user', mode: 'delete', id: u.id })}>D</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="access-section">
        <h2>2. Назначения и правила</h2>
      </section>
      <section className="access-grid access-grid--wide">
        <article className="access-card">
          <div className="card-header-row"><h2>Назначения ролей сотрудникам</h2>
          <button type="button" className="secondary-button" onClick={() => setModalState({ entity: 'employeeRole', mode: 'create' })}>Добавить</button></div>
          <div className="stack-form">
            <input
              value={employeeRoleSearch}
              onChange={(e) => setEmployeeRoleSearch(e.target.value)}
              placeholder="Поиск по сотруднику или роли"
            />
          </div>
          <div className="matrix-wrap matrix-wrap--employee-roles">
            <table className="matrix-table matrix-table--employee-roles">
              <thead>
                <tr>
                  <th className="col-id">ID</th>
                  <th className="col-left">Сотрудник</th>
                  <th className="col-left">Роль</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployeeRoleRows.map((item) => (
                  <tr key={item.id}>
                    <td className="col-id">{item.id}</td>
                    <td className="col-left">{overview.employees.find((e) => e.id === item.employee_id)?.full_name}</td>
                    <td className="col-left">{overview.roles.find((r) => r.id === item.role_id)?.name}</td>
                    <td>
                      <button type="button" className="action-letter-btn action-letter-btn--delete" title="Удалить" aria-label="Удалить" onClick={() => setModalState({ entity: 'employeeRole', mode: 'delete', id: item.id })}>
                        D
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="access-card">
          <div className="card-header-row"><h2>Назначения разрешений ролям</h2><button type="button" className="secondary-button" onClick={onSaveRolePermissionMatrix} disabled={isSavingRolePermissions || selectedModuleFilter === 0}>{isSavingRolePermissions ? 'Сохранение...' : 'Сохранить'}</button></div>
          <div className="stack-form">
            <select value={selectedModuleFilter} onChange={(e) => setSelectedModuleFilter(Number(e.target.value))}>
              <option value={0}>Выбери модуль</option>
              {overview.modules.map((module) => (
                <option key={module.id} value={module.id}>{module.code} - {module.name}</option>
              ))}
            </select>
          </div>
          <div className="matrix-wrap matrix-wrap--role-permissions">
            {selectedModuleFilter === 0 ? (
              <p className="access-matrix-placeholder">Выбери модуль</p>
            ) : (
              <table className="matrix-table matrix-table--role-permissions">
                <thead>
                  <tr>
                    <th className="col-left">Роль</th>
                    {actionCodes.map((code) => (
                      <th key={code}>{code}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {overview.roles.map((role) => (
                    <tr key={role.id}>
                      <td className="col-left">{role.name}</td>
                      {actionCodes.map((code) => {
                        const permissionIds = permissionByCodeForSelectedModule.get(code) || [];
                        const checked = permissionIds.length > 0
                          ? permissionIds.every((permissionId) => Boolean(rolePermissionDraft[`${role.id}:${permissionId}`]))
                          : false;
                        return (
                          <td key={code}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isEnsuringMatrixPermission}
                              title={
                                permissionIds.length === 0 && !checked
                                  ? 'При включении будет создано разрешение в справочнике для выбранного модуля.'
                                  : undefined
                              }
                              onChange={(e) => {
                                void handleRolePermissionMatrixToggle(role.id, code, e.target.checked);
                              }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <h3 className="access-summary-heading">Сводка по модулям (только просмотр)</h3>
          <div className="matrix-wrap matrix-wrap--summary">
            <table className="matrix-table matrix-table--summary">
              <thead>
                <tr>
                  <th className="col-left">Модуль</th>
                  {roleModuleSummaryRows.map(({ role }) => (
                    <th key={role.id}>{role.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {overview.modules.map((module) => (
                  <tr key={module.id}>
                    <td className="col-left" title={`Код: ${module.code}`}>
                      {module.name}
                    </td>
                    {roleModuleSummaryRows.map(({ role, codesByModule }) => {
                      const codes = (codesByModule.get(module.id) || []).slice().sort();
                      return (
                        <td key={role.id}>{codes.length > 0 ? codes.join(', ') : '—'}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="access-card">
          <div className="card-header-row card-header-row--multiline">
            <div className="access-card__heading">
              <h2>Правила доступа ролей к отделам</h2>
              <p className="access-section__hint">Отдел у роли даёт контекст данных: "этот человек из OП/СМО"  и "какие задачи/страницы/модули он видит"</p>
            </div>
            <button
              type="button"
              className="secondary-button"
              onClick={() => void onSaveDepartmentAccessMatrix()}
              disabled={isSavingDepartmentAccess}
            >
              {isSavingDepartmentAccess ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
          <div className="matrix-wrap matrix-wrap--department-access">
            <table className="matrix-table matrix-table--department-access">
              <thead>
                <tr>
                  <th className="col-left">Роль</th>
                  {overview.departments.map((department) => (
                    <th key={department.id} title={`Код: ${department.code}`}>
                      {department.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {overview.roles.map((role) => (
                  <tr key={role.id}>
                    <td className="col-left">{role.name}</td>
                    {overview.departments.map((department) => {
                      const key = `${role.id}:${department.id}`;
                      const entry = overview.department_access.find(
                        (item) => item.role_id === role.id && item.department_id === department.id,
                      );
                      const serverActive = isDepartmentAccessActive(entry);
                      const checked = Object.prototype.hasOwnProperty.call(departmentAccessDraft, key)
                        ? Boolean(departmentAccessDraft[key])
                        : serverActive;
                      return (
                        <td key={department.id}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isSavingDepartmentAccess}
                            onChange={(e) => {
                              const nextChecked = e.target.checked;
                              setDepartmentAccessDraft((prev) => ({
                                ...prev,
                                [key]: nextChecked,
                              }));
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

      </section>

      <section className="access-section">
        <h2>3. Быстрая проверка доступов сотрудника</h2>
        <p className="access-section__hint">
          Выберите сотрудника, чтобы быстро увидеть его роли, разрешения и ограничения по отделам.
        </p>
      </section>
      <section className="access-grid access-grid--wide">
        <article className="access-card">
          <h2>Проверка сотрудника</h2>
          <div className="stack-form">
            <select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(Number(e.target.value))}
              required
            >
              {overview.employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.full_name}
                </option>
              ))}
            </select>
          </div>

          {!selectedEmployee ? (
            <div className="error-banner">Сотрудник не выбран.</div>
          ) : (
            <div className="access-check">
              <div className="access-check__block">
                <h3>Роли</h3>
                {selectedEmployeeRoleIds.length === 0 ? (
                  <p className="access-empty">Роли не назначены.</p>
                ) : (
                  <ul className="simple-list">
                    {selectedEmployeeRoleIds.map((roleId) => (
                      <li key={roleId}>{overview.roles.find((role) => role.id === roleId)?.name || `#${roleId}`}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="access-check__block">
                <h3>Разрешения по модулям</h3>
                {selectedEmployeePermissionGroups.length === 0 ? (
                  <p className="access-empty">Разрешения не найдены.</p>
                ) : (
                  <div className="matrix-wrap">
                    <table className="matrix-table">
                      <thead>
                        <tr>
                          <th>Модуль</th>
                          <th>Разрешения</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployeePermissionGroups.map((group) => (
                          <tr key={group.moduleCode}>
                            <td>{group.moduleCode}</td>
                            <td>{group.permissions.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="access-check__block">
                <h3>Доступы к отделам</h3>
                {selectedEmployeeDepartmentAccessGrouped.length === 0 ? (
                  <p className="access-empty">Ограничения по отделам не заданы.</p>
                ) : (
                  <div className="matrix-wrap">
                    <table className="matrix-table">
                      <thead>
                        <tr>
                          <th>Роль</th>
                          <th>Отделы</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployeeDepartmentAccessGrouped.map((row) => (
                          <tr key={row.roleId}>
                            <td>{row.roleName}</td>
                            <td>{row.departmentsText}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </article>
      </section>

      <section className="access-section"><h2>4. Справочники модели доступов</h2></section>
      <section className="access-grid access-grid--wide">
        <article className="access-card">
          <div className="card-header-row">
            <h2>Роли</h2>
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setRoleForm({ name: '', description: '', is_head: false });
                setModalState({ entity: 'role', mode: 'create' });
              }}
            >
              Добавить
            </button>
          </div>
          <div className="matrix-wrap">
            <table className="matrix-table matrix-table--reference">
              <thead>
                <tr>
                  <th className="col-id sortable" onClick={() => toggleRoleSort('id')}>ID {roleSort.key === 'id' ? (roleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleRoleSort('name')}>Название {roleSort.key === 'name' ? (roleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleRoleSort('description')}>Описание {roleSort.key === 'description' ? (roleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleRoleSort('is_head')}>Руководитель отдела {roleSort.key === 'is_head' ? (roleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-actions">Действия</th>
                </tr>
              </thead>
              <tbody>
                {sortedRoles.map((role) => (
                  <tr key={role.id}>
                    <td className="col-id">{role.id}</td>
                    <td>{role.name}</td>
                    <td>{role.description || '-'}</td>
                    <td>{role.is_head === true ? 'Да' : 'Нет'}</td>
                    <td className="col-actions">
                      <button type="button" className="action-letter-btn action-letter-btn--edit" title="Редактировать" aria-label="Редактировать" onClick={() => openRoleEditModal(role.id)}>E</button>
                      <button type="button" className="action-letter-btn action-letter-btn--delete" title="Удалить" aria-label="Удалить" onClick={() => setModalState({ entity: 'role', mode: 'delete', id: role.id })}>D</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="access-card">
          <div className="card-header-row">
            <h2>Модули</h2>
            <button type="button" className="secondary-button" onClick={() => setModalState({ entity: 'module', mode: 'create' })}>Добавить</button>
          </div>
          <div className="matrix-wrap">
            <table className="matrix-table matrix-table--reference">
              <thead>
                <tr>
                  <th className="col-id sortable" onClick={() => toggleModuleSort('id')}>ID {moduleSort.key === 'id' ? (moduleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleModuleSort('name')}>Название {moduleSort.key === 'name' ? (moduleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleModuleSort('code')}>Код {moduleSort.key === 'code' ? (moduleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleModuleSort('description')}>Описание {moduleSort.key === 'description' ? (moduleSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-actions">Действия</th>
                </tr>
              </thead>
              <tbody>
                {sortedModules.map((item) => (
                  <tr key={item.id}>
                    <td className="col-id">{item.id}</td>
                    <td>{item.name}</td>
                    <td>{item.code}</td>
                    <td>{item.description || '—'}</td>
                    <td className="col-actions">
                      <button type="button" className="action-letter-btn action-letter-btn--edit" title="Редактировать" aria-label="Редактировать" onClick={() => openModuleEditModal(item.id)}>E</button>
                      <button type="button" className="action-letter-btn action-letter-btn--delete" title="Удалить" aria-label="Удалить" onClick={() => setModalState({ entity: 'module', mode: 'delete', id: item.id })}>D</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="access-card">
          <div className="card-header-row">
            <h2>Отделы</h2>
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setDepartmentForm({ name: '', code: '', is_executor: false });
                setModalState({ entity: 'department', mode: 'create' });
              }}
            >
              Добавить
            </button>
          </div>
          <div className="matrix-wrap">
            <table className="matrix-table matrix-table--reference">
              <thead>
                <tr>
                  <th className="col-id sortable" onClick={() => toggleDepartmentSort('id')}>ID {departmentSort.key === 'id' ? (departmentSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleDepartmentSort('name')}>Название {departmentSort.key === 'name' ? (departmentSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleDepartmentSort('code')}>Код {departmentSort.key === 'code' ? (departmentSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="sortable" onClick={() => toggleDepartmentSort('is_executor')}>Исполнитель {departmentSort.key === 'is_executor' ? (departmentSort.direction === 'asc' ? '↑' : '↓') : ''}</th>
                  <th className="col-actions">Действия</th>
                </tr>
              </thead>
              <tbody>
                {sortedDepartments.map((department) => (
                  <tr key={department.id}>
                    <td className="col-id">{department.id}</td>
                    <td>{department.name}</td>
                    <td>{department.code}</td>
                    <td>{department.is_executor === true ? 'Да' : 'Нет'}</td>
                    <td className="col-actions">
                      <button type="button" className="action-letter-btn action-letter-btn--edit" title="Редактировать" aria-label="Редактировать" onClick={() => openDepartmentEditModal(department.id)}>
                        E
                      </button>
                      <button
                        type="button"
                        className="action-letter-btn action-letter-btn--delete"
                        title="Удалить"
                        aria-label="Удалить"
                        onClick={() => setModalState({ entity: 'department', mode: 'delete', id: department.id })}
                      >
                        D
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {modalState && (
        <div className="modal-overlay" role="presentation">
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            key={
              modalState.mode === 'delete'
                ? `delete-${modalState.entity}-${modalState.id ?? 'x'}`
                : `${modalState.mode}-${modalState.entity}-${modalState.id ?? 'new'}`
            }
          >
            <h3>
              {modalState.mode === 'delete'
                ? 'Подтверждение удаления'
                : modalState.mode === 'create'
                  ? 'Создание записи'
                  : 'Редактирование записи'}
            </h3>

            {modalState.mode === 'delete' ? (
              <>
                <p>Удалить запись? Изменение затронет связанные назначения и права доступа.</p>
                <div className="modal-actions"><button type="button" className="text-button" onClick={() => setModalState(null)}>Отмена</button><button type="button" className="danger-button" onClick={onConfirmDelete}>Удалить</button></div>
              </>
            ) : (
              <form className="stack-form" onSubmit={modalState.mode === 'create' ? onSubmitCreateModal : onSubmitEditModal}>
                {modalState.entity === 'role' && (
                  <>
                    <input
                      value={roleForm.name}
                      onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Название роли"
                      required
                    />
                    <textarea
                      value={roleForm.description}
                      onChange={(e) => setRoleForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Описание"
                    />
                    <label className="inline-checkbox">
                      <input
                        type="checkbox"
                        checked={roleForm.is_head === true}
                        onChange={(e) => setRoleForm((prev) => ({ ...prev, is_head: e.target.checked }))}
                      />
                      Руководитель отдела (видит все задачи отдела по связке доступа)
                    </label>
                  </>
                )}

                {modalState.entity === 'module' && (
                  <>
                    <input
                      value={moduleForm.name}
                      onChange={(e) => setModuleForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Название модуля"
                      required
                    />
                    <input
                      value={moduleForm.code}
                      onChange={(e) => setModuleForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      placeholder="Код модуля"
                      required
                    />
                    <textarea
                      value={moduleForm.description}
                      onChange={(e) => setModuleForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Описание"
                    />
                  </>
                )}

                {modalState.entity === 'permission' && (
                  <>
                    <select
                      value={permissionForm.module_id}
                      onChange={(e) => setPermissionForm((prev) => ({ ...prev, module_id: Number(e.target.value) }))}
                      required
                    >
                      {overview.modules.map((module) => (
                        <option key={module.id} value={module.id}>
                          {module.code}
                        </option>
                      ))}
                    </select>
                    <input
                      value={permissionForm.code}
                      onChange={(e) => setPermissionForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      placeholder="Код разрешения"
                      required
                    />
                    <textarea
                      value={permissionForm.description}
                      onChange={(e) => setPermissionForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Описание"
                    />
                  </>
                )}

                {modalState.entity === 'department' && (
                  <>
                    <input
                      value={departmentForm.name}
                      onChange={(e) => setDepartmentForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Название отдела"
                      required
                    />
                    <input
                      value={departmentForm.code}
                      onChange={(e) => setDepartmentForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      placeholder="Код отдела"
                      required
                    />
                    <label className="inline-checkbox">
                      <input
                        type="checkbox"
                        checked={departmentForm.is_executor === true}
                        onChange={(e) => setDepartmentForm((prev) => ({ ...prev, is_executor: e.target.checked }))}
                      />
                      Отдел-исполнитель (можно назначать на задачи)
                    </label>
                  </>
                )}

                {modalState.entity === 'user' && (
                  <>
                    <input
                      value={userForm.username}
                      onChange={(e) => setUserForm((prev) => ({ ...prev, username: e.target.value }))}
                      placeholder="Логин"
                      required
                    />
                    <input
                      value={userForm.full_name}
                      onChange={(e) => setUserForm((prev) => ({ ...prev, full_name: e.target.value }))}
                      placeholder="ФИО"
                      required
                    />
                    <input
                      value={userForm.email}
                      onChange={(e) => setUserForm((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="Email"
                      required
                    />
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm((prev) => ({ ...prev, password: e.target.value }))}
                      placeholder={modalState.mode === 'create' ? 'Пароль' : 'Новый пароль (опционально)'}
                      required={modalState.mode === 'create'}
                    />
                    <label className="inline-checkbox">
                      <input
                        type="checkbox"
                        checked={userForm.is_active}
                        onChange={(e) => setUserForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                      />
                      Активен
                    </label>
                    <label>
                      Согласующий расходы
                      <select
                        value={userForm.expenses_approver_id ?? ''}
                        onChange={(e) => setUserForm((prev) => ({
                          ...prev,
                          expenses_approver_id: e.target.value ? Number(e.target.value) : null,
                        }))}
                      >
                        <option value="">Не назначен</option>
                        {users.map((employee) => (
                            <option key={employee.id} value={employee.id}>
                              {employee.full_name}
                            </option>
                          ))}
                      </select>
                    </label>
                  </>
                )}

                {modalState.entity === 'employeeRole' && (
                  <>
                    <select
                      value={employeeRoleForm.employeeId}
                      onChange={(e) => setEmployeeRoleForm((prev) => ({ ...prev, employeeId: Number(e.target.value) }))}
                    >
                      {overview.employees.map((employee) => (
                        <option key={employee.id} value={employee.id}>
                          {employee.full_name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={employeeRoleForm.roleId}
                      onChange={(e) => setEmployeeRoleForm((prev) => ({ ...prev, roleId: Number(e.target.value) }))}
                    >
                      {overview.roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {modalState.entity === 'rolePermission' && (
                  <>
                    <select
                      value={rolePermissionForm.roleId}
                      onChange={(e) => setRolePermissionForm((prev) => ({ ...prev, roleId: Number(e.target.value) }))}
                    >
                      {overview.roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={rolePermissionForm.permissionId}
                      onChange={(e) => setRolePermissionForm((prev) => ({ ...prev, permissionId: Number(e.target.value) }))}
                    >
                      {overview.permissions.map((permission) => (
                        <option key={permission.id} value={permission.id}>
                          {permission.code} - {permission.description || 'без описания'}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <div className="modal-actions">
                  <button type="button" className="text-button" onClick={() => setModalState(null)}>Отмена</button>
                  <button type="submit" className="secondary-button">{modalState.mode === 'create' ? 'Создать' : 'Сохранить'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControlPage;
